import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { createPortal } from "react-dom";
import {
  createSpinner,
  showSpinner,
  hideSpinner
} from "@syncfusion/ej2-react-popups";
import "./GlobalLoader.css";

interface LoaderContextType {
  show: () => void;
  hide: () => void;
  /**
   * Force the spinner off regardless of how many owners it has. For the
   * defensive "clear anything the previous screen left spinning" call a page
   * makes on mount / unmount — that isn't a `hide()` matching a `show()` of its
   * own, and counting it as one would unbalance the owner it belongs to.
   */
  reset: () => void;
}

const LoaderContext = createContext<LoaderContextType | null>(null);

export const useGlobalLoader = () => {
  const ctx = useContext(LoaderContext);
  if (!ctx) {
    throw new Error("useGlobalLoader must be used within GlobalLoader");
  }
  return ctx;
};

export const GlobalLoader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const spinnerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [target, setTarget] = useState<HTMLElement | null>(null);

  /**
   * How many callers currently want the spinner up.
   *
   * This used to be a plain boolean, so any `hide()` turned it off for
   * everybody. On the write-back board two owners overlap routinely — the
   * write-back workflow holds the loader for the whole issue → report → close →
   * confirm sequence, while a board re-pull shows and hides its own — and the
   * re-pull's `hide()` tore the workflow's spinner down mid-flight, then the
   * next re-pull put it back. That off/on is the flicker: the spinner appearing
   * to load twice at times nothing was being loaded.
   */
  const owners = useRef(0);

  /** ✅ Find the page body once */
  useEffect(() => {
    const el = document.getElementById("page-body");
    if (el) {
      el.style.position = "relative"; // ✅ ensure anchor
      setTarget(el);
    }
  }, []);

  /** ✅ Create spinner once */
useEffect(() => {
  if (!spinnerRef.current || !target) return;

  const el = spinnerRef.current;

  createSpinner({
    target: el,
    cssClass: "e-spin-blue"
  });

  if (visible) {
    showSpinner(el);
  }

  return () => {
    hideSpinner(el);
  };
}, [target]); // ✅ KEY: wait until portal target exists


  /** ✅ Show/hide spinner */
  useEffect(() => {
    if (!spinnerRef.current) return;

    if (visible) {
      showSpinner(spinnerRef.current);
    } else {
      hideSpinner(spinnerRef.current);
    }
  }, [visible]);

  const api = useMemo<LoaderContextType>(
    () => ({
      show: () => {
        owners.current += 1;
        setVisible(true);
      },
      hide: () => {
        owners.current = Math.max(0, owners.current - 1);
        if (owners.current === 0) setVisible(false);
      },
      reset: () => {
        owners.current = 0;
        setVisible(false);
      }
    }),
    []
  );

  return (
    <LoaderContext.Provider value={api}>
      {children}

      {/* ✅ Render INSIDE #page-body (not the whole app!) */}
      {target &&
        createPortal(
          <div
            className="global-loader"
            style={{ display: visible ? "flex" : "none" }}
          >
            <div ref={spinnerRef} />
          </div>,
          target
        )}
    </LoaderContext.Provider>
  );
};
